import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AllocationsService, OPMCalculatorResultsResponse } from 'api';
import { SERVICES_VALUATIONS_REFETCH_ON_WINDOW_FOCUS } from 'common/constants/services';
import { OPM_CALCULATOR_RESULTS_QUERY_KEY } from 'common/constants/services/allocations';
import { OPMCalculatorInputs } from '../types';

export interface UseOPMCalculatorResultsParams {
  inputs: OPMCalculatorInputs;
  /** ISO date the maturity runs from (the valuation's market date). The backend defaults it. */
  opmDate?: string;
}

/**
 * True only when every input the Black-Scholes ladder needs is present and in range. The
 * backend rejects a non-positive maturity or risk-free rate and a negative equity value, so
 * gate here rather than firing a request that is known to fail.
 */
export const areOPMCalculatorInputsComplete = (inputs: OPMCalculatorInputs): boolean => {
  const { capTableId, equityValue, riskFreeRate, maturity, volatility } = inputs;
  return (
    Number.isSafeInteger(capTableId)
    && (capTableId ?? 0) > 0
    && equityValue !== undefined
    && equityValue > 0
    && riskFreeRate !== undefined
    && riskFreeRate > 0
    && maturity !== undefined
    && maturity > 0
    && volatility !== undefined
    && volatility > 0
  );
};

/**
 * Fetches the full OPM ladder for the standalone calculator.
 *
 * `keepPreviousData` is what lets the result tables stay on screen while the analyst edits an
 * input, instead of the page blanking between keystrokes.
 */
const useOPMCalculatorResults = ({ inputs, opmDate }: UseOPMCalculatorResultsParams) => {
  const isEnabled = areOPMCalculatorInputsComplete(inputs);

  const { data, error, isError, isFetching, isLoading } = useQuery<OPMCalculatorResultsResponse>({
    enabled: isEnabled,
    placeholderData: keepPreviousData,
    queryFn: () =>
      AllocationsService.apiAllocationsOpmCalculatorResultsCreate({
        cap_table_id: Number(inputs.capTableId),
        equity_value: String(inputs.equityValue),
        risk_free_rate: String(inputs.riskFreeRate),
        maturity: String(inputs.maturity),
        volatility: String(inputs.volatility),
        opm_date: opmDate ?? null,
      }),
    queryKey: [
      OPM_CALCULATOR_RESULTS_QUERY_KEY,
      inputs.capTableId,
      inputs.equityValue,
      inputs.riskFreeRate,
      inputs.maturity,
      inputs.volatility,
      opmDate,
    ],
    refetchOnWindowFocus: SERVICES_VALUATIONS_REFETCH_ON_WINDOW_FOCUS,
  });

  return {
    results: data,
    error,
    isError,
    isFetching,
    isLoading,
    hasCompleteInputs: isEnabled,
  };
};

export default useOPMCalculatorResults;
